import { useCallback, useMemo, useState, useEffect, useRef } from 'react'

export default function useRespectiveState<State>(
  getInitialState: () => State,
  dependencies: React.DependencyList,
): [State, React.Dispatch<React.SetStateAction<State>>] {
  const defaultValue = useMemo(getInitialState, dependencies)
  const [valuePair, setValuePair] = useState<[React.DependencyList, State] | []>([])

  const dependenciesRef = useRef(dependencies)
  const areDependenciesEqual = dependencies.every(
    (dep, index) => dep === dependenciesRef.current[index],
  )

  useEffect(() => {
    if (!areDependenciesEqual) {
      dependenciesRef.current = dependencies
    }
  }, [dependencies, areDependenciesEqual])

  const value =
    areDependenciesEqual && valuePair[0]?.every((dep, index) => dep === dependencies[index])
      ? (valuePair[1] as State)
      : defaultValue

  const setValue = useCallback(
    (newValue: State | ((previousValue: State) => State)) => {
      if (typeof newValue === 'function') {
        setValuePair(previousValuePair => {
          const previousValue = previousValuePair[0]?.every(
            (dep, index) => dep === dependencies[index],
          )
            ? (previousValuePair[1] as State)
            : defaultValue
          const value = (newValue as (previousValue: State) => State)(previousValue)
          return [dependencies, value]
        })
        return
      }
      setValuePair([dependencies, newValue])
    },
    [dependencies, setValuePair, defaultValue],
  )

  return [value, setValue]
}
